<template>
  <div class="container">
    <component
      :is="useNewDesign ? currentTemplate.newComponent : currentTemplate.component"
      v-if="currentTemplate"
      :is-mirror="currentTemplate.isMirror"
      :is-additional-frame="currentTemplate.isAdditionalFrame"
      :items="tickets"
    />
    <template v-else>
      <h1 class="unknown-template__title">{{ 'registrator_unknown_template' | translate }}</h1>
    </template>
  </div>
</template>

<script>
import InfoscreenTemplates from '../helpers/InfoscreenTemplates';
import TicketCallMode from '../helpers/TicketCallMode';
import FontService from '../../../../services/FontService';
import {parseJSON} from '@/helpers/utils';
import {TICKET_STATUS} from '@/components/devices/Infoscreen/config';
import TICKET_QUEUE_TYPE from '../helpers/TicketQueueType';

export default {
  name: 'Infoscreen',
  data() {
    return {
      tickets: [],
      fontService: new FontService(),
      INFOSCREEN_SOCKET_EVENT: {
        1001: this.onTicketEnqueued,
        1002: this.onAddTicket,
        1003: this.onTicketCall,
        1004: this.onTicketRedirect,
        1006: this.onTicketClose,
        1007: this.onTicketPostponed,
        1008: this.onTicketCancelled,
        1010: this.onTicketReadied,
        2000: this.onSoundCheck
      }
    };
  },
  computed: {
    useNewDesign() {
      return !!this.$store.infoscreenData.useNewTemplateDesign;
    },
    currentTemplate() {
      const deviceTemplate = this.$store.deviceStartData.template;
      return InfoscreenTemplates[deviceTemplate] || null;
    },
    canDisplayOthersTickets() {
      return this.currentTemplate === InfoscreenTemplates.issuing_point_grid;
    }
  },
  mounted() {
    this.$initService.authorizeDeviceByUID(this.$store.deviceUID);
    this.websocketSubscribe();
    this.getTickets();
    this.setCustomFonts();
  },
  destroyed() {
    this.fontService.destroy();
    this.socketMessageUnsubscription();
    this.$store.infoscreenData.actionsQueue.stop();
  },
  methods: {
    setCustomFonts() {
      const companyFonts = this.$store.deviceStartData.firm_fonts;
      const firmStyle = this.$store.deviceStartData.firm_style;

      const customFonts = this.fontService.getCustomFontsByFontIds(companyFonts, {
        normalFontId: firmStyle.font_normal,
        semiBoldFontId: firmStyle.font_semi_bold,
        boldFontId: firmStyle.font_bold
      });

      const customFontsArray = [];

      if (customFonts.fontNormal) {
        this.$store.infoscreenData.fontNormalFamily = customFonts.fontNormal.name;
        customFontsArray.push(customFonts.fontNormal);
      }

      if (customFonts.fontBold) {
        this.$store.infoscreenData.fontBoldFamily = customFonts.fontBold.name;
        customFontsArray.push(customFonts.fontBold);
      }

      if (customFonts.fontSemiBold) {
        this.$store.infoscreenData.fontSemiBoldFamily = customFonts.fontSemiBold.name;
        customFontsArray.push(customFonts.fontSemiBold);
      }

      this.fontService.updateFonts(customFontsArray);
    },
    websocketSubscribe() {
      this.socketMessageUnsubscription = this.$eventEmitter.subscribe(
        this.$eventEmitter.eventTypes.SOCKETS_MESSAGE,
        (response) => {
          if (!response.payload) return;

          // Для чек саунда возвращается пустая строка в payload
          if (response.payload.event_type && response.payload.event_type === 2000) {
            this.INFOSCREEN_SOCKET_EVENT[2000]();
            return;
          }

          parseJSON(response.payload.payload).then((data) => {
            if (this.INFOSCREEN_SOCKET_EVENT[response.payload.event_type]) {
              this.INFOSCREEN_SOCKET_EVENT[response.payload.event_type](data);
            } else {
              console.error(
                `Нет обработчика для event_type = ${response.payload.event_type}`,
                response
              );
            }
          });
        }
      );

      this.socketReconnectUnsubscription = this.$eventEmitter.subscribe(
        this.$eventEmitter.eventTypes.SOCKETS_RECONNECT,
        () => {
          this.$initService.authorizeDeviceByUID(this.$store.deviceUID);
          this.getTickets();
        }
      );
    },
    onTicketCall(data) {
      const transformedTicket = this.tickets.find((ticket) => ticket.id === data.id);

      if (!transformedTicket) {
        console.warn(`Талон ${data.id} не найден`);
        return;
      }

      this.callTicket(
        transformedTicket,
        TicketCallMode.AGAIN,
        this.getTicketQueueType(transformedTicket)
      );
    },
    onAddTicket(ticket) {
      if (this.canDisplayOthersTickets) {
        this.updateTicket(ticket);
      } else {
        this.addTicket(ticket, {withCall: true, callMode: TicketCallMode.FIRST});
      }
    },
    onTicketCancelled(data) {
      this.removeTicketById(data.ticket.id);
    },
    onTicketRedirect(data) {
      if (this.canDisplayOthersTickets) {
        this.updateTicket(data);
      } else {
        this.removeTicketById(data.id);
      }
    },
    onTicketPostponed(data) {
      this.removeTicketById(data.ticket.id);
    },
    onTicketClose(ticket) {
      this.removeTicketById(ticket.id);
    },
    onTicketReadied(ticket) {
      this.updateTicket(ticket);
    },
    onTicketEnqueued(ticket) {
      if (!this.canDisplayOthersTickets) return;

      const transformedTicket = this.transformOthersTicket(ticket);
      if (transformedTicket) {
        this.tickets.push(transformedTicket);
        this.callTicket(transformedTicket, TicketCallMode.FIRST, TICKET_QUEUE_TYPE.OFFICE);
      }
    },
    callTicket(ticket, ticketCallMode, queueType = TICKET_QUEUE_TYPE.WINDOW) {
      this.$nextTick(() => {
        this.$emit('callTicket', {ticket: ticket, ticketCallMode, queueType});
      });
    },
    onSoundCheck() {
      if (!this.$store.deviceStartData.is_sound_enabled) {
        console.warn(`Пришёл сигнал о проверке звука, но на устройстве звук отключен`);
        return;
      }

      const {audioProcessor} = this.$store.infoscreenData;
      this.$store.infoscreenData.externalSoundIsCalling = true;

      this.$nextTick(() => {
        const soundCheckAction = () => {
          return audioProcessor.playAudioSequence(audioProcessor.getAudioByKey('call'));
        };

        this.$store.infoscreenData.actionsQueue.add(soundCheckAction);
        this.$store.infoscreenData.actionsQueue.execute(false, () => {
          this.$store.infoscreenData.externalSoundIsCalling = false;
          console.log(`Sound check finished`);
        });
      });
    },
    removeTicketById(ticketId) {
      this.tickets = this.tickets.filter((ticket) => ticket.id !== ticketId);
    },
    updateTicket(ticket) {
      const existingTicket = this.tickets.find(({id}) => ticket.id === id);
      if (!existingTicket) {
        console.warn(`Талон id: ${ticket.id} не найден для обновления`);
        this.addTicket(ticket, {
          withCall: true,
          callMode: TicketCallMode.FIRST,
          queueType: this.getTicketQueueType(ticket)
        });
        return;
      }

      const transformedTicket = this.canDisplayOthersTickets
        ? this.transformOthersTicket(ticket)
        : this.transformTicket(ticket);

      if (transformedTicket) {
        Object.assign(existingTicket, transformedTicket);

        this.callTicket(
          existingTicket,
          TicketCallMode.FIRST,
          this.getTicketQueueType(transformedTicket)
        );
      }
    },
    getTickets() {
      this.$crudService
        .get(
          `tickets/info_table?office_id=${this.$store.deviceStartData.office_settings.office_id}`
        )
        .then((response) => {
          const tickets = response.data;
          this.tickets = [];
          this.addTickets(tickets);
        });
    },
    addTickets(tickets) {
      tickets.forEach((ticket) => this.addTicket(ticket));
    },
    addTicket(
      ticket,
      call = {withCall: false, callMode: TicketCallMode.FIRST, queueType: TICKET_QUEUE_TYPE.WINDOW}
    ) {
      const transformedTicket = this.canDisplayOthersTickets
        ? this.transformOthersTicket(ticket)
        : this.transformTicket(ticket);

      if (transformedTicket) {
        this.tickets.push(transformedTicket);
        call.withCall && this.callTicket(transformedTicket, call.callMode, call.queueType);
      }
    },
    transformTicket(ticket) {
      const ticketWindow = this.$store.deviceStartData.windows_with_arrows.find(
        (window) => window.id === ticket.last_window_id
      );

      if (!ticketWindow) {
        console.warn(
          `Талон привязан к окну (${ticket.last_window_id}), которое не привязано к текущему инфотабло`
        );
        return null;
      }

      return {...ticket, window: ticketWindow};
    },
    transformOthersTicket(ticket) {
      if (
        ticket.status_type === TICKET_STATUS.WAITING ||
        ticket.status_type === TICKET_STATUS.READIED
      ) {
        return {...ticket, window: ticket.window || {}};
      } else {
        return this.transformTicket(ticket);
      }
    },
    getTicketQueueType(ticket) {
      return ticket.window && 'issuing_point' in ticket.window && !ticket.window.issuing_point
        ? TICKET_QUEUE_TYPE.WINDOW
        : TICKET_QUEUE_TYPE.OFFICE;
    }
  }
};
</script>

<style lang="scss" scoped>
.container {
  height: 100vh;
}
</style>

<style lang="scss">
@import '../styles/reset.scss';
@import '../styles/animations.scss';
@import '../../../../styles/variables.scss';

body {
  font-feature-settings: 'zero' on;
}

.unknown-template__title {
  color: $color-accent-orange;
}
</style>
